// src/hooks/useAudioPlayer.ts
import { useRef, useCallback, useEffect } from 'react';
import { notifyAudioError, watchAudioStart } from '../utils/audioErrors';

interface UseAudioPlayerReturn {
  play: (url: string, onEnded?: () => void) => Promise<void>;
  stop: () => void;
  isPlaying: boolean;
}

const useAudioPlayer = (): UseAudioPlayerReturn => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
      isPlayingRef.current = false;
    }
  }, []);

  const play = useCallback(async (url: string, onEnded?: () => void) => {
    // Stop poprzedniego audio
    stop();

    const newAudio = new Audio(url);
    audioRef.current = newAudio;
    isPlayingRef.current = true;

    // Koniec odtwarzania albo błąd – w obu przypadkach gra idzie dalej, żeby nie utknęła.
    // Pomijamy audio zatrzymane przez stop() albo zastąpione nowym.
    let finished = false;
    const finish = (failed: boolean, error?: unknown) => {
      if (finished || audioRef.current !== newAudio) return;
      finished = true;
      isPlayingRef.current = false;
      if (failed) notifyAudioError(error);
      if (onEnded) onEnded();
    };

    newAudio.onended = () => finish(false);
    newAudio.onerror = () => finish(true);

    try {
      const playPromise = newAudio.play();
      watchAudioStart(newAudio, playPromise, () => finish(true));
      await playPromise;
    } catch (error) {
      console.log('Autoplay blocked or audio error:', error);
      finish(true, error);
    }
  }, [stop]);

  // Cleanup on unmount
  useEffect(() => () => {
    stop();
  }, [stop]);

  return {
    play,
    stop,
    isPlaying: isPlayingRef.current,
  };
};

export default useAudioPlayer;
